import React, { useEffect, useState } from 'react';
import { Helmet } from 'react-helmet';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import styled from 'styled-components';
import Skeleton from 'react-loading-skeleton';
import useRequireLogin from '../../hooks/useRequireLogin';
import { getWorkshopBookings } from '../../services/workshopBookings';
import AccountNav from '../../components/customerArea/shared/AccountNav';
import WorkshopBookingsList from '../../components/customerArea/workshopBookings/WorkshopBookingsList';
import Row from '../../components/shared/atoms/Row';
import Box from '../../components/shared/atoms/Box';
import Container from '../../components/shared/atoms/Container';
import Heading from '../../components/shared/atoms/Heading';

const ErrorBox = styled.div`
  padding: 14px;
  margin: 16px 0;
  background: ${(p) => p.theme.colors.red.pale};
  color: ${(p) => p.theme.colors.red.dark};
  border-radius: 8px;
  font-size: 14px;
`;

const WorkshopBookingsSkeleton = () => (
  <Box data-testid="workshop-bookings-skeleton">
    <Skeleton style={{ borderRadius: 8 }} height={150} />
    <Skeleton style={{ borderRadius: 8, marginTop: 24 }} height={150} />
  </Box>
);

/**
 * FIXERLIGHT-052 lot 1 — « Mes rendez-vous d'atelier », placée au même
 * niveau de navigation que « Mes rendez-vous » RJ existant
 * (`src/scenes/customerArea/Account.jsx`), pas nichée dedans : un client
 * peut avoir des courses RJ et des rendez-vous d'atelier séparément, chacun
 * avec son propre historique.
 */
function WorkshopBookings() {
  const { t } = useTranslation();
  const client = useSelector((s) => s.client);
  useRequireLogin();
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  useEffect(() => {
    if (!client.id) return;
    let cancelled = false;
    (async () => {
      setLoading(true);
      setError(false);
      try {
        const data = await getWorkshopBookings(client.id);
        if (cancelled) return;
        setBookings(data);
      } catch (e) {
        if (cancelled) return;
        setError(true);
        setBookings([]);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [client.id]);

  return (
    <Container>
      <Container fluid pb={10} pt={4}>
        <Helmet
          title={t('workshopappointments.meta.title')}
          meta={[{ name: 'robots', content: 'noindex, nofollow' }]}
        />
        <Row md gap={56}>
          <AccountNav />
          <Box mt={{ xs: -7, lg: 0 }} style={{ flex: 1 }}>
            <Heading level={2} mb={{ xs: 4, lg: 6 }}>
              {t('workshopappointments.title')}
            </Heading>
            {loading ? (
              <WorkshopBookingsSkeleton />
            ) : error ? (
              <ErrorBox data-testid="workshop-bookings-error">
                {t('workshopappointments.error')}
              </ErrorBox>
            ) : (
              <WorkshopBookingsList bookings={bookings} />
            )}
          </Box>
        </Row>
      </Container>
    </Container>
  );
}

export default WorkshopBookings;
